import NavResponsiveModal, {
    ModalActions,
    ModalContent,
    ModalHeader,
} from '@/components/Navigation/NavResponsiveModal';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { Grid, MenuItem, Popper, TextField, Tooltip } from '@material-ui/core';
import React, { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { OnwardCheckbox } from '@/components/ShipmentForm/blocks';
import { Info } from '@material-ui/icons';
import { useLazyQuery } from '@apollo/client';
import { GET_RACKS } from '../graphql';
import { Alert, AlertTitle, Autocomplete } from '@material-ui/lab';
import { enumerateWarehouseBins } from '@/utilities/warehouseRacks';
import { useClientUser } from '@/hooks';

const StorePallets = ({ pallets, loading, callbacks, warehouseId, manifest }) => {
    const [locations, setLocations] = useState({});
    const [stagingZone, setStagingZone] = useState('');
    const [dirty, setDirty] = useState({});
    const [isPool, setIsPool] = useState({});
    const [binOptions, setBinOptions] = useState({});
    const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
    const { locations: warehouses } = useClientUser();

    const needsWarehouse = !!manifest && !warehouseId;
    const effectiveWarehouseId = warehouseId || selectedWarehouseId;

    // const isValid = useMemo(() => {
    //     return Object.fromEntries(
    //         Object.entries(locations).map(([pallet_id, location]) => [
    //             pallet_id,
    //             /^.*-[1-9][0-9]*-[1-9][0-9]*(-[1-9])?$/.test(location),
    //         ])
    //     );
    // }, [locations]);

    const [getBinOptions] = useLazyQuery(GET_RACKS, {
        onCompleted: ({ location }) => {
            // Use the shared bin enumeration so these dropdown options match the
            // printed/scanned bin labels byte-for-byte (the source of truth).
            const allBins = enumerateWarehouseBins(location.warehouse_racks);
            setBinOptions({
                poolLocations: allBins
                    .filter((bin) => bin.isPool)
                    .map((bin) => ({ label: bin.label, isPool: true })),
                bins: allBins
                    .filter((bin) => !bin.isPool)
                    .map((bin) => ({ label: bin.label, isPool: false })),
            });
        },
        onError: (error) => {
            console.error('Error fetching bin options:', error);
        },
    });

    useEffect(() => {
        setLocations(
            Object.fromEntries((pallets || []).map((pallet) => [pallet.pallet_id, pallet.warehouse_location || '']))
        );
        setIsPool(Object.fromEntries((pallets || []).map((pallet) => [pallet.pallet_id, pallet.is_pool || false])));

        setBinOptions({});
        if (effectiveWarehouseId) {
            getBinOptions({ variables: { location_id: effectiveWarehouseId } });
        }
    }, [pallets, effectiveWarehouseId]);

    useEffect(() => {
        if (manifest) {
            setStagingZone(manifest.staging_zone);
        }
    }, [manifest]);

    const onClose = () => {
        setLocations({});
        setDirty({});
        setIsPool({});
        setSelectedWarehouseId('');
        callbacks?.setStagingZoneManifest && callbacks.setStagingZoneManifest(null);
        callbacks.storePalletsInit(null);
    };

    const submit = () => {
        if (manifest) {
            callbacks.saveStagingZone(manifest, stagingZone, needsWarehouse ? selectedWarehouseId : null).then(() => {
                onClose();
            });
        } else {
            callbacks
                .storePallets(
                    pallets
                        .filter(
                            (pallet) =>
                                locations[pallet.pallet_id] !== pallet.warehouse_location ||
                                isPool[pallet.pallet_id] !== pallet.is_pool
                        )
                        .map((pallet) => ({
                            ...pallet,
                            warehouse_location: locations[pallet.pallet_id],
                            is_pool: isPool[pallet.pallet_id],
                        }))
                )
                .then(() => {
                    onClose();
                });
        }
    };

    return (
        <NavResponsiveModal open={!!pallets?.length || manifest} onClose={onClose}>
            <ModalHeader title={manifest ? 'Set Staging Zone' : 'Store'} />
            <ModalContent>
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex-wrap: nowrap;
                        ${needsWarehouse ? 'min-height: 160px;' : 'height: 160px;'}
                    `}
                >
                    {needsWarehouse &&
                        ((warehouses || []).length > 0 ? (
                            <>
                                <Alert
                                    severity="warning"
                                    css={css`
                                        margin-bottom: 1rem;
                                    `}
                                >
                                    <AlertTitle>No warehouse associated with this manifest yet</AlertTitle>
                                    Choose the warehouse this manifest is staged from. It will be saved to the manifest
                                    along with the staging zone.
                                </Alert>
                                <TextField
                                    select
                                    required
                                    variant="outlined"
                                    label="Warehouse"
                                    value={selectedWarehouseId}
                                    onChange={(e) => {
                                        setSelectedWarehouseId(e.target.value);
                                        setStagingZone('');
                                    }}
                                    style={{ width: '300px', marginBottom: '1rem' }}
                                >
                                    {warehouses.map((warehouse) => (
                                        <MenuItem key={warehouse.location_id} value={warehouse.location_id}>
                                            {warehouse.location_name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </>
                        ) : (
                            <Alert severity="warning">
                                <AlertTitle>No warehouse associated with this manifest yet</AlertTitle>
                                Add a warehouse to your account before setting a staging zone.
                            </Alert>
                        ))}
                    {(pallets || []).map((pallet, idx) => (
                        <React.Fragment key={pallet.pallet_id}>
                            <Autocomplete
                                freeSolo={true}
                                getOptionLabel={(option) => option?.label?.toString()}
                                options={
                                    isPool[pallet.pallet_id] ? binOptions?.poolLocations || [] : binOptions?.bins || []
                                }
                                onChange={(event, newValue) => {
                                    if (newValue) {
                                        setLocations((prev) => ({ ...prev, [pallet.pallet_id]: newValue?.label }));
                                    }
                                }}
                                onInputChange={(event, newInputValue) => {
                                    setLocations((prev) => ({ ...prev, [pallet.pallet_id]: newInputValue }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        value={locations[pallet.pallet_id] || ''}
                                        label="New Location"
                                    />
                                )}
                                PopperComponent={({ children, ...popperProps }) => (
                                    <Popper {...popperProps} style={{ width: '300px' }}>
                                        {children}
                                    </Popper>
                                )}
                                ListboxProps={{ style: { maxHeight: '150px' } }}
                                style={{ width: '300px' }}
                            />
                            <Grid container direction="row" alignItems="center">
                                <OnwardCheckbox
                                    label={'Floor/Pool Location'}
                                    checked={isPool[pallet.pallet_id] || false}
                                    onChange={(e) =>
                                        setIsPool((prev) => ({ ...prev, [pallet.pallet_id]: e.target.checked }))
                                    }
                                />
                                <Tooltip
                                    style={{ color: '#59B863', marginLeft: '5px' }}
                                    title={
                                        'Check this box to select/show general "floor" locations in the warehouse. This will allow you to use the same storage location name for multiple pallets.'
                                    }
                                    placement="right"
                                >
                                    <Info />
                                </Tooltip>
                            </Grid>
                        </React.Fragment>
                    ))}
                    {manifest && effectiveWarehouseId && (
                        <Autocomplete
                            key={effectiveWarehouseId}
                            freeSolo={true}
                            getOptionLabel={(option) =>
                                typeof option === 'string' ? option : option?.label?.toString() || ''
                            }
                            options={binOptions?.poolLocations || []}
                            onChange={(event, newValue) => {
                                if (newValue) {
                                    setStagingZone(typeof newValue === 'string' ? newValue : newValue.label);
                                }
                            }}
                            onInputChange={(event, newInputValue) => {
                                setStagingZone(newInputValue);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    value={stagingZone || ''}
                                    label="New Location"
                                />
                            )}
                            PopperComponent={({ children, ...popperProps }) => (
                                <Popper {...popperProps} style={{ width: '300px' }}>
                                    {children}
                                </Popper>
                            )}
                            ListboxProps={{ style: { maxHeight: '150px' } }}
                            style={{ width: '300px' }}
                        />
                    )}
                </Grid>
            </ModalContent>
            <ModalActions>
                <SecondaryButton disabled={loading.palletLoading} onClick={onClose}>
                    Cancel
                </SecondaryButton>
                <PrimaryButton
                    // disabled={loading.palletLoading || Object.values(isValid).some((x) => !x)}
                    disabled={loading.palletLoading || (needsWarehouse && !selectedWarehouseId)}
                    onClick={submit}
                >
                    Done
                </PrimaryButton>
            </ModalActions>
        </NavResponsiveModal>
    );
};

export default StorePallets;
